import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Auto-rotating "recognized by editors" banner.
 *
 * Crossfades the left image, body copy and reviewer logo of each slide in sync.
 * Autoplay honors `prefers-reduced-motion`, pauses on hover/focus and when the
 * tab is hidden.
 *
 * @extends {Component}
 */
class EditorRecognitionComponent extends Component {
  /** @type {number} */
  #index = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {boolean} */
  #playing = false;

  /** @type {boolean} */
  #pointerInside = false;

  /** @type {boolean} */
  #focusInside = false;

  /** @type {number} */
  #interval = 5000;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  connectedCallback() {
    super.connectedCallback();

    this.#interval = Math.max(2000, (Number(this.dataset.interval) || 5) * 1000);
    this.#reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#reducedMotion.addEventListener('change', this.#handleMotionPreference);

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibility);

    this.#index = this.#initialIndex();
    this.#render();

    if (this.#canAutoplay()) this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopTimer();
    this.#reducedMotion?.removeEventListener('change', this.#handleMotionPreference);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#index = Math.min(this.#index, Math.max(0, this.#slides.length - 1));
    this.#render();
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.editor-recognition__slide')]);
  }

  /** @returns {HTMLElement[]} */
  get #mediaItems() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.editor-recognition__media-item')]);
  }

  /** @returns {HTMLElement[]} */
  get #dots() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.editor-recognition__dot')]);
  }

  /** @returns {number} */
  #initialIndex() {
    const active = this.#slides.findIndex((slide) => slide.classList.contains('is-active'));
    return active < 0 ? 0 : active;
  }

  /** @returns {boolean} */
  #canAutoplay() {
    if (this.dataset.autoplay !== 'true') return false;
    if (this.#slides.length <= 1) return false;
    return !this.#reducedMotion?.matches;
  }

  /**
   * Navigate to a specific slide (from a pagination dot). Re-arms the timer so
   * the slide the visitor chose stays visible for the full interval.
   *
   * @param {string | number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();
    this.#setIndex(Number(index));
    if (this.#playing) this.#startTimer();
  }

  play() {
    if (this.#slides.length <= 1) return;
    this.#playing = true;
    this.#startTimer();
  }

  pause() {
    this.#playing = false;
    this.#stopTimer();
  }

  /** @param {number} index */
  #setIndex(index) {
    const count = this.#slides.length;
    if (count === 0) return;
    this.#index = ((index % count) + count) % count;
    this.#render();
  }

  #render() {
    const slides = this.#slides;
    const mediaItems = this.#mediaItems;
    const dots = this.#dots;

    for (const [i, slide] of slides.entries()) {
      const isActive = i === this.#index;
      slide.classList.toggle('is-active', isActive);
      // Keep hidden slides out of the accessibility tree AND the tab order.
      // `aria-hidden` alone leaves the slide's role="group" label and any
      // rich-text links reachable by Tab (announced as silence, WCAG 4.1.2),
      // so pair it with `inert` — matching slideshow.js's setSlideHidden().
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('inert', !isActive);
    }

    // Media items are deduplicated by image, so activate the one matching the
    // current slide's image key. Slides that share an image keep the same media
    // element active, which avoids re-triggering the crossfade (no flicker).
    const activeSlide = slides[this.#index];
    const activeImageKey = activeSlide?.dataset.imageKey ?? null;
    // Focal point is a per-slide setting, but the media element is shared across
    // slides that reuse the same image (it only bakes in the first such slide's
    // focus). Apply the active slide's focal point here so later slides sharing
    // the image still crop to their own focus.
    const activeFocalPoint = activeSlide?.dataset.imageFocalPoint || 'center';

    for (const media of mediaItems) {
      const isActive = media.dataset.imageKey === activeImageKey;
      media.classList.toggle('is-active', isActive);
      if (isActive) media.style.setProperty('--er-object-position', activeFocalPoint);
    }

    for (const [i, dot] of dots.entries()) {
      const isActive = i === this.#index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    }
  }

  #startTimer() {
    this.#stopTimer();
    if (!this.#playing || this.#slides.length <= 1) return;
    this.#timer = window.setInterval(() => this.#setIndex(this.#index + 1), this.#interval);
  }

  #stopTimer() {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #suspend = () => {
    this.#stopTimer();
  };

  /**
   * Restart autoplay only when nothing is holding it paused. Hover and keyboard
   * focus are tracked independently, so the interval must stay stopped while the
   * pointer is still inside the banner even after focus leaves (and vice versa).
   * Otherwise focus leaving during hover — or the mouse leaving while a control
   * still has focus — would restart rotation the visitor did not expect.
   */
  #resume = () => {
    if (this.#playing && !this.#pointerInside && !this.#focusInside) this.#startTimer();
  };

  #handlePointerEnter = () => {
    this.#pointerInside = true;
    this.#suspend();
  };

  #handlePointerLeave = () => {
    this.#pointerInside = false;
    this.#resume();
  };

  #handleFocusIn = () => {
    this.#focusInside = true;
    this.#suspend();
  };

  /**
   * Clear the focus-hold and resume only when focus truly leaves the component.
   * `focusout` bubbles, so it also fires when focus moves between internal
   * controls (e.g. pagination dots); in that case `relatedTarget` is the element
   * receiving focus and is still inside `this`, so focus has not actually left.
   *
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;
    this.#focusInside = false;
    this.#resume();
  };

  #handleVisibility = () => {
    if (document.hidden) {
      this.#stopTimer();
    } else {
      this.#resume();
    }
  };

  #handleMotionPreference = () => {
    // Reduced motion turned on: stop rotating. Turned off again: resume
    // autoplay if `data-autoplay` is still enabled and there is more than one
    // slide (`#canAutoplay()` re-checks the current preference).
    if (this.#reducedMotion?.matches) {
      this.pause();
    } else if (this.#canAutoplay()) {
      this.play();
    }
  };
}

if (!customElements.get('editor-recognition-component')) {
  customElements.define('editor-recognition-component', EditorRecognitionComponent);
}
